import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiService } from '../services/api';

const StudentJoin: React.FC = () => {
  const [sessionCode, setSessionCode] = useState('');
  const [studentName, setStudentName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checkingSession, setCheckingSession] = useState(true);
  const [sessionInfo, setSessionInfo] = useState<any>(null);
  const navigate = useNavigate();

  // Vérifier s'il y a une session active au démarrage
  useEffect(() => {
    const checkActiveSession = async () => {
      try {
        const storedStudentInfo = localStorage.getItem('studentInfo');
        if (storedStudentInfo) {
          const studentInfo = JSON.parse(storedStudentInfo);
          
          // Vérifier si la session existe toujours et est active
          try {
            const sessionResponse = await ApiService.getSession(studentInfo.sessionId);
            if (sessionResponse.success && sessionResponse.session) {
              const session = sessionResponse.session;
              
              // Vérifier si la session est active (created ou active)
              setSessionInfo({
                ...studentInfo,
                sessionName: session.name,
                sessionStatus: session.status,
                participantsCount: session.participantsCount
              });
            } else {
              // Session introuvable, nettoyer le localStorage
              localStorage.removeItem('studentInfo');
            }
          } catch (err) {
            // Erreur lors de la vérification, nettoyer le localStorage
            localStorage.removeItem('studentInfo');
          }
        }
      } catch (err) {
        console.error('Erreur lors de la vérification de session:', err);
      } finally {
        setCheckingSession(false);
      }
    };

    checkActiveSession();
  }, []);

  const handleRestoreSession = () => {
    if (sessionInfo) {
      navigate(`/student/session/${sessionInfo.sessionId}`);
    }
  };

  const handleNewSession = () => {
    setSessionInfo(null);
    localStorage.removeItem('studentInfo');
  };

  const handleJoinSession = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!sessionCode.trim() || !studentName.trim()) {
      setError('Veuillez remplir tous les champs');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Générer un ID stable basé sur le nom et la session pour éviter les doublons
      const sessionCodeUpper = sessionCode.trim().toUpperCase();
      const studentNameTrim = studentName.trim();
      const studentId = `student_${sessionCodeUpper}_${studentNameTrim.toLowerCase().replace(/\s+/g, '_')}`;
      
      const response = await ApiService.joinSession(sessionCodeUpper, studentId, studentNameTrim);
      
      if (response.success) {
        // Sauvegarder les infos de l'étudiant dans le localStorage
        localStorage.setItem('studentInfo', JSON.stringify({
          id: studentId,
          name: studentName.trim(),
          sessionId: response.session.id,
          sessionCode: response.session.code
        }));
        
        // Rediriger vers la page de session
        navigate(`/student/session/${response.session.id}`);
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la connexion à la session');
    } finally {
      setLoading(false);
    }
  };

  // Afficher le loader pendant la vérification
  if (checkingSession) {
    return (
      <div className="app-container">
        <header className="header">
          <div className="header-content">
            <h1 className="logo">Vérification...</h1>
          </div>
        </header>
        <main className="main-content">
          <div className="card" style={{ maxWidth: '500px', margin: '0 auto', textAlign: 'center' }}>
            <div className="card-content">
              <div className="spinner" style={{ width: '40px', height: '40px', borderWidth: '4px', margin: '0 auto 1rem' }}></div>
              <p>🔍 Vérification de votre session active...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="app-container">
      <header className="header">
        <div className="header-content">
          <h1 className="logo">Rejoindre une session</h1>
          <div className="header-actions">
            <button 
              className="btn btn-outline btn-sm"
              onClick={() => navigate('/')}
            >
              ← Accueil
            </button>
          </div>
        </div>
      </header>

      <main className="main-content">
        {/* Afficher la session active si elle existe */}
        {sessionInfo && (
          <div className="card" style={{ maxWidth: '600px', margin: '0 auto 2rem', border: '2px solid #28a745' }}>
            <div className="card-header">
              <h2 className="card-title">🔄 Session active détectée</h2>
            </div>
            <div className="card-content">
              <div className="p-3" style={{ 
                background: '#d4edda', 
                color: '#155724', 
                borderRadius: '8px',
                border: '1px solid #c3e6cb'
              }}>
                <h3 style={{ margin: '0 0 1rem 0', color: '#155724' }}>
                  📚 {sessionInfo.sessionName}
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <strong>👤 Étudiant:</strong><br />
                    {sessionInfo.name}
                  </div>
                  <div>
                    <strong>🔑 Code session:</strong><br />
                    <span style={{ 
                      fontFamily: 'monospace', 
                      background: '#fff', 
                      padding: '2px 6px', 
                      borderRadius: '4px',
                      border: '1px solid #ccc'
                    }}>
                      {sessionInfo.sessionCode}
                    </span>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <strong>📊 Statut:</strong><br />
                    <span style={{ 
                      padding: '2px 8px', 
                      borderRadius: '12px',
                      background: sessionInfo.sessionStatus === 'active' ? '#28a745' : '#6c757d',
                      color: 'white',
                      fontSize: '0.9em'
                    }}>
                      {sessionInfo.sessionStatus === 'active' ? '🟢 Active' : sessionInfo.sessionStatus === 'ended' ? '🔴 Terminée' : '🟡 En attente'}
                    </span>
                  </div>
                  <div>
                    <strong>👥 Participants:</strong><br />
                    {sessionInfo.participantsCount || 0}
                  </div>
                </div>
              </div>
            </div>
            <div className="card-actions" style={{ display: 'flex', gap: '1rem' }}>
              <button 
                className="btn btn-secondary"
                onClick={handleRestoreSession}
                style={{ flex: 1 }}
              >
                🔄 Rejoindre ma session
              </button>
              <button 
                className="btn btn-outline"
                onClick={handleNewSession}
                style={{ flex: 1 }}
              >
                🆕 Nouvelle session
              </button>
            </div>
          </div>
        )}
        <div className="card" style={{ maxWidth: '500px', margin: '0 auto' }}>
          <div className="card-header">
            <h2 className="card-title">
              {sessionInfo ? '🆕 Rejoindre une nouvelle session' : '🎓 Rejoindre une session'}
            </h2>
          </div>
          
          <form onSubmit={handleJoinSession}>
            <div className="card-content">
              {error && (
                <div className="mb-3 p-2" style={{ 
                  background: '#f8d7da', 
                  color: '#721c24', 
                  borderRadius: '4px',
                  border: '1px solid #f5c6cb'
                }}>
                  ❌ {error}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="sessionCode" className="form-label">
                  Code de session
                </label>
                <input
                  type="text"
                  id="sessionCode"
                  className="form-input"
                  placeholder="Ex: ABC123"
                  value={sessionCode}
                  onChange={(e) => setSessionCode(e.target.value.toUpperCase())}
                  maxLength={6}
                  style={{ textTransform: 'uppercase', letterSpacing: '0.1em' }}
                />
                <small className="form-error">
                  Le code vous a été fourni par votre enseignant
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="studentName" className="form-label">
                  Votre nom
                </label>
                <input
                  type="text"
                  id="studentName"
                  className="form-input"
                  placeholder="Votre nom complet"
                  value={studentName}
                  onChange={(e) => setStudentName(e.target.value)}
                />
              </div>

              <div className="p-2" style={{ 
                background: '#d1ecf1', 
                color: '#0c5460', 
                borderRadius: '4px',
                border: '1px solid #bee5eb'
              }}>
                <small>
                  💡 <strong>Astuce:</strong> Demandez le code de session à votre enseignant. 
                  Il s'affiche généralement au début du cours ou sur l'écran de présentation.
                </small>
              </div>
            </div>

            <div className="card-actions">
              <button 
                type="submit" 
                className="btn btn-secondary"
                disabled={loading || !sessionCode.trim() || !studentName.trim()}
                style={{ width: '100%' }}
              >
                {loading ? (
                  <>
                    <div className="spinner" style={{ width: '20px', height: '20px', borderWidth: '2px' }}></div>
                    Connexion...
                  </>
                ) : (
                  '🚀 Rejoindre la session'
                )}
              </button>
            </div>
          </form>
        </div>

        <div className="card mt-4">
          <div className="card-header">
            <h3 className="card-title">📋 Instructions</h3>
          </div>
          <div className="card-content">
            <ol style={{ paddingLeft: '1.5rem' }}>
              <li className="mb-2">
                <strong>Obtenez le code:</strong> Votre enseignant vous donnera un code à 6 caractères (ex: ABC123)
              </li>
              <li className="mb-2">
                <strong>Entrez vos informations:</strong> Saisissez le code et votre nom complet
              </li>
              <li className="mb-2">
                <strong>Rejoignez:</strong> Cliquez sur "Rejoindre la session" pour accéder aux questions
              </li>
              <li className="mb-2">
                <strong>Répondez:</strong> Attendez que l'enseignant active une question, puis répondez
              </li>
            </ol>
          </div>
        </div>
      </main>
    </div>
  );
};

export default StudentJoin;
